import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Registro } from '../types';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import DateFilter from '../components/DateFilter';
import { useReportConfig } from '../hooks/useReportConfig';
import { useEnhancedPDF } from '../hooks/useEnhancedPDF';
import { Select, Button as AntButton, Space, Divider as AntDivider, message } from 'antd';
import { FileTextOutlined, DownloadOutlined } from '@ant-design/icons';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import { actualizarCategoria, borrarCategoria } from '../services/api';
import { EditOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
import { Modal } from '@mui/material';
import { Spin } from 'antd';
import { Row, Col, Card as AntCard, Typography as AntTypography } from 'antd';
import { useRondasData } from '../hooks/useRondasData';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

interface ReportesProps {
  registros: Registro[];
}

interface RegistroAgrupado {
  macro: string;
  registros: Registro[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

// Paleta de 13 colores bien diferenciados
const COLORS = [
  '#0088FE', // Azul brillante
  '#FFBB28', // Amarillo dorado
  '#FF8042', // Naranja fuerte
  '#00C49F', // Verde agua
  '#FF4F81', // Rosa fuerte
  '#A28FD0', // Lila / violeta
  '#FF6F00', // Naranja oscuro
  '#4DD0E1', // Celeste
  '#AF19FF', // Púrpura intenso
  '#B0DE09', // Verde lima
  '#FFB300', // Amarillo intenso
  '#E6194B', // Rojo vibrante
  '#3CB44B', // Verde clásico
];

const defaultPeriodos = [
  { value: 'mes_actual', label: 'Mes actual' },
  { value: 'mes_anterior', label: 'Mes anterior' },
  { value: 'anio_actual', label: 'Año actual' },
  { value: 'Personalizado', label: 'Rango personalizado' },
];

// Utilidad para cargar imágenes como base64
function getBase64FromUrl(url: string): Promise<string> {
  return fetch(url)
    .then(response => response.blob())
    .then(
      blob =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    );
}

const MACRO_CATEGORIAS = [
  'Mascotas',
  'Vehículos',
  'Infraestructura',
  'Iluminación',
  'Ruido',
  'Convivencia',
  'Seguridad',
  'Limpieza',
  'Servicios',
  'Sin novedad',
  'Sin categoría',
];
const CATEGORIA_A_MACRO: { [key: string]: string } = {
  'Deposición de mascotas': 'Mascotas',
  'Mascota sin correa': 'Mascotas',
  'Vehículo mal estacionado': 'Vehículos',
  'Objeto indebido': 'Infraestructura',
  'Uso de área fuera de horario': 'Convivencia',
  'Mal uso de implementos': 'Infraestructura',
  'Fumar en área no permitida': 'Convivencia',
  'Alto volumen en la residencia': 'Ruido',
  'Elevador mantenimiento': 'Infraestructura',
  'Falta de iluminación': 'Iluminación',
  'Sin novedad': 'Sin novedad',
  // Puedes agregar más según lo necesites
};

const Reportes: React.FC<ReportesProps> = ({ registros }) => {
  const [tabValue, setTabValue] = useState(0);
  const [registrosAgrupados, setRegistrosAgrupados] = useState<RegistroAgrupado[]>([]);
  const [periodo, setPeriodo] = useState('mes_actual');
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      startDate: start.toISOString().slice(0, 10),
      endDate: today.toISOString().slice(0, 10),
    };
  });
  const [alertaPDF, setAlertaPDF] = useState<string>('');
  const [editandoCategoriaId, setEditandoCategoriaId] = useState<string | null>(null);
  const [categoriaTemporal, setCategoriaTemporal] = useState<string>('');
  const [guardandoCategoria, setGuardandoCategoria] = useState<string | null>(null);
  const [imagenAmpliada, setImagenAmpliada] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  // Nuevo sistema de templates
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('default');
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const { templates, getTemplate } = useReportConfig();
  const { generateEnhancedPDF } = useEnhancedPDF();

  useEffect(() => {
    let start: Date, end: Date;
    const today = new Date();
    if (periodo === 'mes_actual') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = today;
    } else if (periodo === 'mes_anterior') {
      const prevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      start = prevMonth;
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (periodo === 'anio_actual') {
      start = new Date(today.getFullYear(), 0, 1);
      end = today;
    } else {
      start = new Date(dateRange.startDate);
      end = new Date(dateRange.endDate);
    }
    setDateRange({
      startDate: start.toISOString().slice(0, 10),
      endDate: end.toISOString().slice(0, 10),
    });
  }, [periodo]);

  useEffect(() => {
    const registrosFiltrados = registros.filter(r => {
      const fecha = new Date(r.fecha);
      return fecha >= new Date(dateRange.startDate) && fecha <= new Date(dateRange.endDate);
    });
    const grupos: { [macro: string]: Registro[] } = {};
    registrosFiltrados.forEach(registro => {
      // Si tiene la incidencia "sin novedad" pero no tiene categoría, ponerlo en "Sin categoría"
      let macro = CATEGORIA_A_MACRO[registro.categoria || ''] || '';
      if (!registro.categoria || macro === '') macro = 'Sin categoría';
      if (!grupos[macro]) grupos[macro] = [];
      grupos[macro].push(registro);
    });
    const agrupados = MACRO_CATEGORIAS.map(macro => ({
      macro,
      registros: (grupos[macro] || []).sort(
        (a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()
      ),
    })).filter(g => g.registros.length > 0);
    setRegistrosAgrupados(agrupados);
    setLoading(false);
    // console.log('Registros agrupados por macro-categoría:', agrupados);
  }, [registros, dateRange]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const totalIncidencias = registrosAgrupados.reduce((sum, g) => sum + g.registros.length, 0);
  const top3CategoriasEspecificas = () => {
    const conteo: { [cat: string]: number } = {};
    registrosAgrupados.forEach(g =>
      g.registros.forEach(r => {
        const cat = r.categoria || r.incidencia || 'Sin categoría';
        // Normaliza para evitar variantes de 'sin novedad' (espacios, mayúsculas)
        const normalizado = cat.trim().toLowerCase().replace(/\s+/g, ' ');
        if (normalizado !== 'sin novedad') {
          conteo[cat] = (conteo[cat] || 0) + 1;
        }
      })
    );
    return Object.entries(conteo)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([cat, cantidad]) => ({ cat, cantidad }));
  };

  const incidenciasPorLugar = () => {
    const conteo: { [key: string]: number } = {};
    registrosAgrupados.forEach(g =>
      g.registros.forEach(r => {
        conteo[r.lugar] = (conteo[r.lugar] || 0) + 1;
      })
    );
    return Object.entries(conteo)
      .map(([lugar, cantidad]) => ({ lugar, cantidad }))
      .sort((a, b) => b.cantidad - a.cantidad);
  };
  const lugarTop = incidenciasPorLugar()[0]?.lugar || '-';
  const diasConMasIncidencias = () => {
    const conteo: { [key: string]: number } = {};
    registrosAgrupados.forEach(g =>
      g.registros.forEach(r => {
        const dia = r.fecha.slice(0, 10);
        conteo[dia] = (conteo[dia] || 0) + 1;
      })
    );
    return Object.entries(conteo)
      .map(([dia, cantidad]) => ({ dia, cantidad }))
      .sort((a, b) => b.cantidad - a.cantidad);
  };
  const diaTop = diasConMasIncidencias()[0]?.dia || '-';

  const dataBarras = registrosAgrupados
    .filter(g => g.macro !== 'Sin novedad' && g.macro !== 'Sin categoría')
    .map(g => ({ macro: g.macro, cantidad: g.registros.length }));
  const dataPie = incidenciasPorLugar();
  // --- Datos para gráfico de tendencia diaria o mensual ---
  let dataLine: { dia: string; cantidad: number }[] = [];
  if (periodo === 'anio_actual') {
    // Tendencia mensual (por mes)
    const conteoMes: { [mes: string]: number } = {};
    registrosAgrupados.forEach(g =>
      g.registros.forEach(r => {
        const fecha = r.fecha.slice(0, 7); // "YYYY-MM"
        conteoMes[fecha] = (conteoMes[fecha] || 0) + 1;
      })
    );
    // Ordenar por mes ascendente
    dataLine = Object.entries(conteoMes)
      .map(([mes, cantidad]) => ({ dia: mes, cantidad }))
      .sort((a, b) => a.dia.localeCompare(b.dia));
  } else {
    // Tendencia diaria (por día)
    dataLine = diasConMasIncidencias()
      .map(d => ({ dia: d.dia, cantidad: d.cantidad }))
      .sort((a, b) => {
        const diaA = parseInt(a.dia.slice(8, 10), 10);
        const diaB = parseInt(b.dia.slice(8, 10), 10);
        return diaA - diaB;
      });
  }

  const generarPDFMensual = async () => {
    try {
      const doc = new jsPDF();
      // Logo: mantener proporciones
      const logoUrl = '/logo.png';
      let y = 15;
      try {
        const imgData = await getBase64FromUrl(logoUrl);
        const img = new window.Image();
        img.src = imgData;
        await new Promise(resolve => {
          img.onload = resolve;
        });
        const aspectRatio = img.width / img.height;
        const logoHeight = 15; // mm
        const logoWidth = logoHeight * aspectRatio;
        doc.addImage(imgData, 'PNG', 14, y, logoWidth, logoHeight);
      } catch (e) {
        // Si falla el logo, continuar sin logo
      }
      // Título y cabecera
      doc.setFontSize(18);
      doc.setTextColor(33, 33, 33);
      // Título alineado a la derecha
      doc.text('REPORTE DE INCIDENCIAS', 196, y + 10, { align: 'right' });
      doc.setDrawColor(0, 136, 254);
      doc.setLineWidth(1);
      doc.line(14, y + 15, 196, y + 15); // línea divisoria azul debajo del título
      // --- NUEVO: Nombre del cliente debajo de la línea ---
      doc.setFontSize(12);
      doc.setTextColor(33, 33, 33);
      // Buscar el nombre del cliente en los registros
      let nombreCliente = 'Cliente';
      if (registros && registros.length > 0) {
        // Buscar el primer registro con campo cliente no vacío
        const regConCliente = registros.find(r => r.cliente && r.cliente.trim() !== '');
        if (regConCliente) {
          nombreCliente = regConCliente.cliente;
        }
      }
      doc.text(`Cliente: ${nombreCliente}`, 14, y + 23);
      doc.setFontSize(12);
      doc.setTextColor(33, 33, 33);
      doc.text(`Periodo: ${dateRange.startDate} a ${dateRange.endDate}`, 14, y + 29);
      doc.setFontSize(11);
      // Resumen en 3 líneas
      doc.text(`Total incidencias: ${totalIncidencias}`, 14, y + 37);
      doc.text(`Lugar con más incidencias: ${lugarTop}`, 14, y + 43);
      doc.text(`Día con más incidencias: ${diaTop}`, 14, y + 49);
      // Agrupar registros válidos por macro-categoría
      const registrosPorMacro: { [macro: string]: any[][] } = {};
      registrosAgrupados.forEach(g =>
        g.registros.forEach(r => {
          const cat = r.categoria || r.incidencia || '';
          const normalizado = cat.trim().toLowerCase().replace(/\s+/g, ' ');
          // Excluir 'sin novedad' y registros sin categoría válida
          if (
            normalizado !== 'sin novedad' &&
            r.categoria &&
            r.categoria.trim() !== '' &&
            r.categoria.toLowerCase() !== 'null'
          ) {
            // Determinar macro-categoría
            let macro = 'Sin categoría';
            if (CATEGORIA_A_MACRO[cat]) {
              macro = CATEGORIA_A_MACRO[cat];
            }
            if (!registrosPorMacro[macro]) registrosPorMacro[macro] = [];
            // Formato de fecha y hora (ej: 2025-04-18 14:30 p.m.)
            let fechaHora = r.fecha;
            if (r.fecha) {
              const d = new Date(r.fecha);
              const yyyy = d.getFullYear();
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const dd = String(d.getDate()).padStart(2, '0');
              let hh = d.getHours();
              const min = String(d.getMinutes()).padStart(2, '0');
              const ampm = hh >= 12 ? 'p.m.' : 'a.m.';
              hh = hh % 12;
              if (hh === 0) hh = 12;
              fechaHora = `${yyyy}-${mm}-${dd} ${hh}:${min} ${ampm}`;
            }
            registrosPorMacro[macro].push([fechaHora, r.lugar, r.incidencia]);
          }
        })
      );
      // Ordenar registros de cada macro-categoría por fecha ascendente (más viejo a más nuevo)
      Object.entries(registrosPorMacro).forEach(([_, filas]) => {
        filas.sort((a, b) => {
          // a[0] y b[0] son las fechas en formato 'YYYY-MM-DD hh:mm a.m./p.m.'
          // Convertir a Date para comparar
          const parseFecha = (f: string) => {
            // Extraer solo la parte de fecha y hora en 24h
            const [fecha, hora, ampm] = f.split(/\s+/);
            let [h, m] = hora.split(':').map(Number);
            if (ampm === 'p.m.' && h !== 12) h += 12;
            if (ampm === 'a.m.' && h === 12) h = 0;
            return new Date(`${fecha}T${String(h).padStart(2, '0')}:${m}:00`);
          };
          return parseFecha(a[0]).getTime() - parseFecha(b[0]).getTime();
        });
      });
      // Añadir cada grupo de macro-categoría como una tabla
      let startY = y + 52;
      let algunaTabla = false;
      Object.entries(registrosPorMacro).forEach(([macro, filas]) => {
        if (filas.length === 0) return;
        algunaTabla = true;
        // Título: solo el nombre de la macro-categoría y cantidad
        autoTable(doc, {
          startY,
          head: [[`${macro} (${filas.length})`]],
          body: [],
          theme: 'plain',
          styles: {
            fontStyle: 'bold',
            fontSize: 12,
            textColor: [0, 136, 254],
            halign: 'left',
            cellPadding: 2,
          },
          headStyles: {
            fillColor: [255, 255, 255],
            textColor: [0, 136, 254],
            fontStyle: 'bold',
            fontSize: 12,
            halign: 'left',
            cellPadding: 2,
          },
          margin: { left: 14, right: 14 },
        });
        startY = (doc as any).lastAutoTable.finalY + 2;
        // Tabla sin columna de Observaciones, ancho extendido
        autoTable(doc, {
          startY,
          head: [['Fecha', 'Lugar', 'Incidencia']],
          body: filas,
          theme: 'grid',
          headStyles: {
            fillColor: [0, 136, 254],
            textColor: 255,
            fontStyle: 'bold',
            halign: 'center',
          },
          alternateRowStyles: { fillColor: [240, 248, 255] },
          styles: { fontSize: 8, cellPadding: 2 },
          columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 45 }, 2: { cellWidth: 75 } },
          margin: { left: 14, right: 14 },
          didDrawPage: () => {
            // Pie de página
            const pageHeight = doc.internal.pageSize.height;
            doc.setFontSize(9);
            doc.setTextColor(120);
            doc.text(
              'Servicios Estratégicos de Protección y Vigilancia Integral S. A. (Seprovinsa Security)',
              14,
              pageHeight - 8
            );
            doc.setTextColor(120);
            doc.text(`Generado: ${new Date().toLocaleString()}`, 14, pageHeight - 3);
          },
        });
        startY = (doc as any).lastAutoTable.finalY + 8;
      });
      if (!algunaTabla) {
        setAlertaPDF('No hay datos para exportar en el periodo seleccionado.');
        return;
      }
      doc.save(`reporte_incidencias_${dateRange.startDate}_${dateRange.endDate}.pdf`);
      setAlertaPDF('¡PDF generado y descargado exitosamente!');
      setTimeout(() => setAlertaPDF(''), 3000);
    } catch (e: any) {
      setAlertaPDF('Ocurrió un error al generar el PDF: ' + (e.message || e));
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Generar PDF con template personalizado
  const generarPDFPersonalizado = async () => {
    setGeneratingPDF(true);
    try {
      const template = getTemplate(selectedTemplateId);
      if (!template) {
        message.error('Template no encontrado');
        return;
      }

      // Filtrar registros del periodo actual
      const registrosActuales = registrosAgrupados.flatMap(g => g.registros);

      // Obtener registros del periodo anterior para comparativas
      let registrosPeriodoAnterior: Registro[] = [];
      if (periodo === 'mes_actual') {
        const mesAnterior = new Date();
        mesAnterior.setMonth(mesAnterior.getMonth() - 1);
        const startAnterior = new Date(mesAnterior.getFullYear(), mesAnterior.getMonth(), 1);
        const endAnterior = new Date(mesAnterior.getFullYear(), mesAnterior.getMonth() + 1, 0);
        
        registrosPeriodoAnterior = registros.filter(r => {
          const fecha = new Date(r.fecha);
          return fecha >= startAnterior && fecha <= endAnterior;
        });
      }

      const comparison = registrosPeriodoAnterior.length > 0 ? {
        currentPeriod: registrosActuales,
        previousPeriod: registrosPeriodoAnterior,
      } : undefined;

      const filename = `reporte_${template.name.toLowerCase().replace(/\s+/g, '_')}_${dateRange.startDate}_${dateRange.endDate}.pdf`;

      await generateEnhancedPDF(registrosActuales, template, comparison, filename);
      
      message.success('PDF generado y descargado exitosamente');
    } catch (error) {
      console.error('Error al generar PDF:', error);
      message.error('Error al generar el PDF');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleGuardarCategoria = async (registro: Registro) => {
    setGuardandoCategoria(String(registro.id));
    try {
      let nuevaCategoria = categoriaTemporal;
      if (nuevaCategoria === '__BORRAR__') {
        await borrarCategoria(Number(registro.id));
        nuevaCategoria = '';
      } else {
        await actualizarCategoria(Number(registro.id), nuevaCategoria);
      }

      // Actualiza el estado local sin recargar la página
      setRegistrosAgrupados(prev => {
        // Elimina el registro de todos los grupos
        let actualizados = prev
          .map(grupo => ({
            ...grupo,
            registros: grupo.registros.filter(r => r.id !== registro.id),
          }))
          .filter(grupo => grupo.registros.length > 0);

        // Si no es borrado, agrégalo al grupo correspondiente
        if (nuevaCategoria !== '') {
          const macro = CATEGORIA_A_MACRO[nuevaCategoria] || 'Sin categoría';
          // Busca si ya existe el grupo
          let grupo = actualizados.find(g => g.macro === macro);
          if (grupo) {
            grupo.registros = [{ ...registro, categoria: nuevaCategoria }, ...grupo.registros];
          } else {
            actualizados.push({
              macro,
              registros: [{ ...registro, categoria: nuevaCategoria }],
            });
          }
        }
        return actualizados;
      });
      // Opcional: podrías mostrar un mensaje de éxito aquí
    } catch (error) {
      // console.error('Error al guardar categoría:', error);
      // alert('Hubo un error al guardar la categoría. Intenta nuevamente.');
    } finally {
      setEditandoCategoriaId(null);
      setGuardandoCategoria(null);
    }
  };

  // Obtener el nombre del cliente
  const { cliente } = useRondasData(periodo);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      {cliente && (
        <AntTypography.Title
          level={2}
          style={{
            fontWeight: 'bold',
            margin: 0,
            background: 'rgb(24, 144, 255)',
            padding: '4px 12px',
            borderRadius: 12,
            color: 'white',
            marginBottom: 24,
          }}
        >
          {cliente}
        </AntTypography.Title>
      )}
      <Container
        className="reportes-rounded"
        maxWidth={false}
        sx={{ maxWidth: 'none', width: '100%' }}
      >
        {loading ? (
          <div
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}
          >
            <Spin size="large" tip="Cargando..." />
          </div>
        ) : (
          <Box sx={{ mt: 3, mb: 4, width: '100%', maxWidth: 'none' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                width: '100%',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <AntTypography.Title level={3} style={{ margin: 0, color: 'white' }}>
                Administrador
              </AntTypography.Title>
              
              <Space size="middle" wrap>
                <Select
                  value={selectedTemplateId}
                  onChange={setSelectedTemplateId}
                  style={{ width: 250 }}
                  placeholder="Seleccionar template"
                >
                  {templates.map(t => (
                    <Select.Option key={t.id} value={t.id}>
                      <FileTextOutlined /> {t.name}
                    </Select.Option>
                  ))}
                </Select>
                
                <AntButton
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={generarPDFPersonalizado}
                  loading={generatingPDF}
                  size="large"
                >
                  Generar PDF Personalizado
                </AntButton>
                
                <AntDivider type="vertical" style={{ height: 32, background: '#fff3' }} />
                
                <Button 
                  variant="outlined" 
                  onClick={generarPDFMensual}
                  sx={{ color: 'white', borderColor: 'white' }}
                >
                  PDF Simple
                </Button>
              </Space>
            </Box>
            {alertaPDF && (
              <Box sx={{ mb: 2 }}>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor:
                      alertaPDF.includes('error') || alertaPDF.includes('No hay datos')
                        ? '#ffeaea'
                        : '#eaffea',
                    color: '#222',
                    border: '1px solid #bbb',
                  }}
                >
                  <AntTypography.Text>{alertaPDF}</AntTypography.Text>
                </Paper>
              </Box>
            )}
            <DateFilter
              registros={registros}
              periodo={periodo}
              setPeriodo={setPeriodo}
              dateRange={dateRange}
              setRangeStart={date =>
                setDateRange({ ...dateRange, startDate: date.format('YYYY-MM-DD') })
              }
              setRangeEnd={date =>
                setDateRange({ ...dateRange, endDate: date.format('YYYY-MM-DD') })
              }
              onDateChange={(start, end) =>
                setDateRange({
                  startDate: start.format('YYYY-MM-DD'),
                  endDate: end.format('YYYY-MM-DD'),
                })
              }
              periodOptions={defaultPeriodos}
            />
            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
              <Col xs={24} md={6}>
                <AntCard
                  bordered
                  style={{
                    borderRadius: 12,
                    background: '#fff',
                    minHeight: 120,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <AntTypography.Text type="secondary">Total incidencias</AntTypography.Text>
                  <AntTypography.Title level={3} style={{ margin: 0 }}>
                    {totalIncidencias}
                  </AntTypography.Title>
                </AntCard>
              </Col>
              <Col xs={24} md={6}>
                <AntCard
                  bordered
                  style={{
                    borderRadius: 12,
                    background: '#fff',
                    minHeight: 120,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    maxHeight: 150,
                    overflow: 'hidden',
                  }}
                >
                  <AntTypography.Text type="secondary">Top 3 incidencias</AntTypography.Text>
                  {top3CategoriasEspecificas()
                    .slice(0, 3)
                    .map((t, index) => (
                      <AntTypography.Text key={t.cat + '-' + index} style={{ display: 'block' }}>
                        {t.cat}: {t.cantidad}
                      </AntTypography.Text>
                    ))}
                  {top3CategoriasEspecificas().length > 3 && (
                    <AntTypography.Text style={{ color: '#888', fontSize: 12 }}>
                      +{top3CategoriasEspecificas().length - 3} más
                    </AntTypography.Text>
                  )}
                </AntCard>
              </Col>
              <Col xs={24} md={6}>
                <AntCard
                  bordered
                  style={{
                    borderRadius: 12,
                    background: '#fff',
                    minHeight: 120,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <AntTypography.Text type="secondary">
                    Lugar con más incidencias
                  </AntTypography.Text>
                  <br />
                  <AntTypography.Text>{lugarTop}</AntTypography.Text>
                </AntCard>
              </Col>
              <Col xs={24} md={6}>
                <AntCard
                  bordered
                  style={{
                    borderRadius: 12,
                    background: '#fff',
                    minHeight: 120,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <AntTypography.Text type="secondary">Día con más incidencias</AntTypography.Text>
                  <br />
                  <AntTypography.Text>{diaTop}</AntTypography.Text>
                </AntCard>
              </Col>
            </Row>

            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
              <Col xs={24} md={8}>
                <AntCard bordered style={{ borderRadius: 12, height: 320 }}>
                  <AntTypography.Text type="secondary">
                    Incidencias por macro-categoría
                  </AntTypography.Text>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={dataBarras}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="macro" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="cantidad" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </AntCard>
              </Col>
              <Col xs={24} md={8}>
                <AntCard bordered style={{ borderRadius: 12, height: 320 }}>
                  <AntTypography.Text type="secondary">Incidencias por lugar</AntTypography.Text>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={dataPie}
                        dataKey="cantidad"
                        nameKey="lugar"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        label
                      >
                        {dataPie.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </AntCard>
              </Col>
              <Col xs={24} md={8}>
                <AntCard bordered style={{ borderRadius: 12, height: 320 }}>
                  <AntTypography.Text type="secondary">
                    {periodo === 'anio_actual' ? 'Tendencia mensual' : 'Tendencia diaria'}
                  </AntTypography.Text>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={dataLine}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="dia"
                        tickFormatter={d =>
                          periodo === 'anio_actual'
                            ? (() => {
                                // Formato: "YYYY-MM" a "Ene", "Feb", ...
                                const meses = [
                                  'Ene',
                                  'Feb',
                                  'Mar',
                                  'Abr',
                                  'May',
                                  'Jun',
                                  'Jul',
                                  'Ago',
                                  'Sep',
                                  'Oct',
                                  'Nov',
                                  'Dic',
                                ];
                                const m = parseInt(d.slice(5, 7), 10);
                                return meses[m - 1] || d;
                              })()
                            : d.slice(8, 10)
                        }
                      />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="cantidad" stroke="#8884d8" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </AntCard>
              </Col>
            </Row>
            <Paper sx={{ width: '100%', mb: 2, maxWidth: 'none' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant={isMobile ? 'scrollable' : 'fullWidth'}
                scrollButtons={isMobile ? 'auto' : false}
                aria-label="Categorías de incidencias"
              >
                {registrosAgrupados.map((grupo, index) => (
                  <Tab
                    key={grupo.macro + '-' + index}
                    label={`${grupo.macro} (${grupo.registros.length})`}
                    id={`tab-${index}`}
                    aria-controls={`tabpanel-${index}`}
                  />
                ))}
              </Tabs>
              {registrosAgrupados.map((grupo, index) => (
                <TabPanel key={grupo.macro + '-' + index} value={tabValue} index={index}>
                  <Grid container spacing={3}>
                    {grupo.registros.map(registro => (
                      <Grid item xs={12} sm={6} md={4} key={registro.id}>
                        <Card
                          key={registro.id + '-' + (registro.categoria || '')}
                          variant="outlined"
                        >
                          {registro.fotoUrl && (
                            <CardMedia
                              component="img"
                              height="200"
                              image={registro.fotoUrl}
                              alt={registro.incidencia}
                              sx={{ objectFit: 'cover', cursor: 'pointer' }}
                              onClick={() => setImagenAmpliada(registro.fotoUrl ?? null)}
                            />
                          )}
                          <CardContent>
                            <AntTypography.Text
                              type="secondary"
                              style={{ fontSize: 14, display: 'block' }}
                            >
                              {registro.fecha.slice(0, 10)}
                            </AntTypography.Text>
                            <AntTypography.Text style={{ fontSize: 16, display: 'block' }}>
                              {registro.lugar}
                            </AntTypography.Text>
                            <AntTypography.Text
                              type="secondary"
                              style={{ fontSize: 14, display: 'block' }}
                            >
                              {registro.incidencia}
                            </AntTypography.Text>
                            {registro.observaciones && (
                              <AntTypography.Text
                                type="secondary"
                                style={{ fontSize: 14, marginTop: 8, display: 'block' }}
                              >
                                {registro.observaciones}
                              </AntTypography.Text>
                            )}
                            <Box sx={{ mt: 2, mb: 1 }}>
                              {editandoCategoriaId === String(registro.id) ? (
                                <select
                                  value={categoriaTemporal ?? ''}
                                  onChange={e => setCategoriaTemporal(e.target.value)}
                                  disabled={guardandoCategoria === String(registro.id)}
                                >
                                  <option value="" disabled>
                                    Seleccionar...
                                  </option>
                                  {/* Opciones únicas del mapeo */}
                                  {Object.keys(CATEGORIA_A_MACRO).map((cat: string) => (
                                    <option key={cat} value={cat}>
                                      {cat}
                                    </option>
                                  ))}
                                  {/* Solo agrega "Sin novedad" si NO está en el mapeo */}
                                  {!Object.keys(CATEGORIA_A_MACRO).includes('Sin novedad') && (
                                    <option value="Sin novedad">Sin novedad</option>
                                  )}
                                  {/* Opción especial para borrar */}
                                  <option value="__BORRAR__">Borrar categoría</option>
                                </select>
                              ) : (
                                <Tag
                                  color={
                                    registro.categoria === 'Sin novedad'
                                      ? 'green'
                                      : registro.categoria
                                        ? 'blue'
                                        : 'default'
                                  }
                                  style={{
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                    fontSize: 16,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    marginTop: 8,
                                    marginBottom: 8,
                                  }}
                                  onClick={() => {
                                    setEditandoCategoriaId(String(registro.id));
                                    setCategoriaTemporal(
                                      registro.categoria || registro.incidencia || ''
                                    );
                                  }}
                                  title="Haz click para editar"
                                >
                                  {registro.categoria || registro.incidencia || 'Sin categoría'}
                                  <EditOutlined style={{ marginLeft: 8 }} />
                                </Tag>
                              )}
                            </Box>
                            {editandoCategoriaId === String(registro.id) && (
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => handleGuardarCategoria(registro)}
                                disabled={guardandoCategoria === String(registro.id)}
                              >
                                Guardar
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </TabPanel>
              ))}
            </Paper>
          </Box>
        )}
        {/* Botón flotante para volver arriba */}
        {showScrollTop && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 32,
              right: 32,
              zIndex: 1200,
              bgcolor: 'primary.main',
              color: 'white',
              borderRadius: '50%',
              boxShadow: 3,
              width: 56,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'opacity 0.3s',
              opacity: 0.85,
              '&:hover': { opacity: 1, bgcolor: 'primary.dark' },
            }}
            onClick={scrollToTop}
            title="Volver al inicio"
          >
            <KeyboardArrowUpIcon sx={{ fontSize: 36 }} />
          </Box>
        )}
        {/* Modal para ampliar imagen */}
        <Modal
          open={!!imagenAmpliada}
          onClose={() => setImagenAmpliada(null)}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <img
            src={imagenAmpliada || ''}
            alt="Evidencia ampliada"
            style={{
              maxHeight: '90vh',
              maxWidth: '90vw',
              borderRadius: 8,
              boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
            }}
          />
        </Modal>
      </Container>
    </>
  );
};

export default Reportes;
